import React from 'react';
import PropTypes from 'prop-types';
import { useSnackbarContext } from 'Context/SnackbarContext';
import usePlaidNewLink from 'Hooks/Aggregators/useNewPlaidLink';
import { INVALID_LINK_TOKEN } from 'Components/UI/PlaidComponent';
import SentryScopedError from 'Errors/SentryScopedError';
import relationships from 'facet-rest/src/consts/relationships';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import fr from 'facet-rest';
import useEventTrackingLogEvent, { categories, eventSubTypes, eventTypes } from 'EventTracking/useEventTrackingLogEvent';

const PlaidLinkContext = React.createContext(undefined);

export const SYNC_STATUS_MAP = {
  waiting: 'waiting',
  syncing: 'syncing',
  finished: 'finished',
};

const SYNC_STARTED = 'sync-started';
const SYNC_FINISHED = 'sync-finished';

const getInitialState = () => {
  return {
    syncStatus: SYNC_STATUS_MAP.waiting,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case SYNC_STARTED: {
      return {
        ...state,
        syncStatus: SYNC_STATUS_MAP.syncing,
      };
    }
    case SYNC_FINISHED: {
      return {
        ...state,
        syncStatus: SYNC_STATUS_MAP.finished,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

class PlaidComponentError extends SentryScopedError {
  constructor(message, sentryScope, ...params) {
    super(sentryScope, ...params);

    this.name = 'PlaidComponentError';
    this.message = message;
  }
}

const PlaidLinkProvider = ({
  resource,
  children,
  onLoadLink,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, getInitialState);
  const { householdResource } = useSimpleHousehold();
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const onSuccess = async () => {
    await onLoadLink(resource.relationship(fr.relationships.onSuccess.name).link('related'));
  };

  const handleFailure = React.useCallback(async (metadata) => {
    logStandardizedEvent(categories.addAccounts, eventTypes.linkAccount, eventSubTypes.failure, {
      eventProperties: {
        aggregator: 'plaid',
        plaidInstitutionId: metadata.institution.institution_id,
        plaidInstitutionName: metadata.institution.name,
        selectedInstitution: resource.attr('institutionName'),
      },
    });
    await onLoadLink(resource.relationship(fr.relationships.onFailure.name).link('related'));
  }, []);

  const handleSuccess = React.useCallback(async (publicToken, metadata) => {
    const tokenCollection = await resource.relationship(relationships.plaidLink.name).link('related').get();
    const tokenResource = tokenCollection.create();
    tokenResource.attr('token', publicToken);

    try {
      dispatch({ type: SYNC_STARTED });
      await tokenResource.submit({ linkArgs: { raw: true } });
      await onSuccess();
      dispatch({ type: SYNC_FINISHED });
      displaySuccess('Successfully synced accounts');
      logStandardizedEvent(categories.addAccounts, eventTypes.linkAccount, eventSubTypes.success, {
        eventProperties: {
          aggregator: 'plaid',
          plaidInstitutionId: metadata.institution.institution_id,
          plaidInstitutionName: metadata.institution.name,
          selectedInstitution: resource.attr('institutionName'),
        },
      });
    } catch (e) {
      await handleFailure(metadata);
      dispatch({ type: SYNC_FINISHED });
      displayErrorMessage(e, 'Failed to sync account');
    }
  }, []);

  const handleExit = React.useCallback(async (err, metadata) => {
    if (err != null && err.error_code === INVALID_LINK_TOKEN) {
      window.Plaid?.destroy();
    }

    // On cancel this is an empty error object, so check if type set before showing snackbar.
    if (err && err.error_type) {
      await handleFailure(metadata);
      const { error_message, ...rest } = err; // eslint-disable-line camelcase
      displayErrorMessage(
        new PlaidComponentError(error_message, {
          ...rest,
          ...metadata,
        }),
        'Failed to sync account',
      );
    }
  }, []);

  const handleEvent = React.useCallback(async (eventName, metadata) => {
    if (eventName !== 'TRANSITION_VIEW' && eventName !== 'SEARCH_INSTITUTION') { // These are useless events, it just says what screen they are on.
      // Log the event to our plaidEvent table, wrap in a try catch so we do not blow up on fail. This is a background process
      try {
        householdResource.relationship('plaidEvent').link('related').post({ attributes: { eventName, ...metadata } }, { deprecatedAllowAlterOfRelation: true });
      } catch (e) {
        // eslint-disable-next-line
        console.log('error logging plaid event', e);
      }
    }
  }, []);

  const {
    handleClickInitiate,
    isInitialized,
    initializationError,
  } = usePlaidNewLink(resource, {
    onExit: handleExit,
    onEvent: handleEvent,
    onSuccess: handleSuccess,
  });

  const handleClick = React.useCallback(async () => {
    logStandardizedEvent(categories.addAccounts, eventTypes.linkAccount, eventSubTypes.initializing, {
      eventProperties: {
        aggregator: 'plaid',
        selectedInstitution: resource.attr('institutionName'),
      },
    });
    await handleClickInitiate();
  }, [handleClickInitiate]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitialized,
      initializationError,
      handleClick,
      isSyncing: state.syncStatus === SYNC_STATUS_MAP.syncing,
    };
  }, [
    state,
    isInitialized,
    initializationError,
    handleClick,
  ]);

  return (
    <PlaidLinkContext.Provider value={memoizedValue}>
      {children}
    </PlaidLinkContext.Provider>
  );
};

const usePlaidLinkContext = () => {
  const context = React.useContext(PlaidLinkContext);
  if (context === undefined) {
    throw new Error('usePlaidLinkContext must be used within a PlaidLinkProvider');
  }
  return context;
};

PlaidLinkProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  resource: PropTypes.object.isRequired,
  onLoadLink: PropTypes.func.isRequired,
};

export {
  PlaidLinkProvider,
  usePlaidLinkContext,
};
